import React, { useState } from 'react';
import VolumeChips from './VolumeChips';
import VolumeDetailsDialog from './VolumeDetailsDialog';

/**
 * VolumeManager - A complete volume management component that combines
 * volume chips display with details dialog functionality.
 * Designed to be reusable across different cloud providers.
 * 
 * @param {Object} props
 * @param {Array} props.volumes - Array of volume objects
 * @param {Array} props.availableStorageConfigs - Array of available storage configurations
 * @param {Function} props.onVolumeUpdate - Function to handle volume updates
 * @param {Function} props.onVolumeDelete - Function to handle volume deletion
 * @param {number} props.nodeGroupIndex - The index of the node group
 * @param {Object} props.chipContainerStyle - Additional styling for the chips container
 * @param {boolean} props.showActions - Whether to show action buttons (default: true)
 */
const VolumeManager = ({
    volumes = [],
    availableStorageConfigs = [],
    onVolumeUpdate,
    onVolumeDelete,
    nodeGroupIndex,
    chipContainerStyle = {},
    showActions = true
}) => {
    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedVolume, setSelectedVolume] = useState(null);
    const [selectedStorageConfig, setSelectedStorageConfig] = useState(null);
    const [isEditable, setIsEditable] = useState(false);

    // Handle view volume details
    const handleViewVolume = (volume, storageConfig, nodeIndex) => {
        setSelectedVolume(volume);
        setSelectedStorageConfig(storageConfig);
        setIsEditable(false);
        setDialogOpen(true);
    };

    // Handle edit volume details
    const handleEditVolume = (volume, storageConfig, nodeIndex) => {
        setSelectedVolume(volume);
        setSelectedStorageConfig(storageConfig);
        setIsEditable(true);
        setDialogOpen(true);
    };

    // Handle delete volume
    const handleDeleteVolume = (nodeIndex, volumeId) => {
        if (onVolumeDelete) {
            onVolumeDelete(nodeIndex, volumeId);
        }
    };

    // Handle close dialog
    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedVolume(null);
        setSelectedStorageConfig(null);
        setIsEditable(false);
    };

    // Handle save volume changes
    const handleSaveVolume = (updatedVolume, nodeIndex) => {
        if (onVolumeUpdate) {
            onVolumeUpdate(updatedVolume, nodeIndex);
        }
    };

    return (
        <>
            <VolumeChips
                volumes={volumes}
                availableStorageConfigs={availableStorageConfigs}
                onView={handleViewVolume}
                onEdit={handleEditVolume}
                onDelete={handleDeleteVolume}
                nodeGroupIndex={nodeGroupIndex}
                style={chipContainerStyle}
                showActions={showActions}
            />
            
            <VolumeDetailsDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                onSave={handleSaveVolume}
                volume={selectedVolume}
                storageConfig={selectedStorageConfig}
                isEditable={isEditable}
                nodeGroupIndex={nodeGroupIndex}
            />
        </>
    );
};

export default VolumeManager;
